import {
  sanitizeClipboardHtml,
  sanitizeClipboardText,
} from './sanitizeHtml';
import { applyInlineMarkdown, inlineMd } from './inlineMarkdown';
import { convertBraceVarsToPills } from '../variableUtils';
import { ensureEditableCaretForInsert } from '../atomicCaret';
import { separateNextUndoStep } from '../undoSeparation';

/**
 * onPaste handler for the editor's contenteditable blocks. Replaces the
 * default paste with sanitized content inserted at the caret, then forces
 * an `input` event so react-contenteditable's onChange → commit path runs
 * (DocEditor owns Ctrl+Z; native undo is always preventDefault'd).
 *
 * Plain / chat-style clips often put literal "**bold**" in text/html as well
 * as text/plain — convert those markers when the HTML has no real <b>/<i>/<code>.
 * Literal `{{name}}` tokens become variable pills.
 */
export const handleEditablePaste = (e) => {
  const clipboard = e.clipboardData;
  if (!clipboard) return; // let the default happen if we can't read it

  const html = clipboard.getData('text/html') || '';
  const plain = clipboard.getData('text/plain') || '';
  // A whole-block copy (data-doc-block) is handled by the editor-level paste
  // listener — never flatten it into text here
  if (html.includes('data-doc-block')) {
    e.preventDefault();
    return;
  }

  e.preventDefault();
  // Prefer HTML when it carries real inline marks, anchors, or editor tokens
  // (variable pills / mentions) — otherwise a copied hyperlink's text/plain
  // URL would win and the <a> would be lost, and a resolved variable would
  // paste as bare display text instead of a live {{name}} pill.
  const htmlHasStructuredInline =
    /<(b|strong|i|em|code|a)\b/i.test(html) ||
    /data-doc-variable/i.test(html) ||
    /data-doc-math/i.test(html) ||
    /data-doc-mention/i.test(html);
  let sanitized;
  // Prefer plain text (with markdown / URL conversion) when HTML has no real
  // structured inline — covers chat pastes and copies that dropped wrappers.
  if (plain && (!html.trim() || !htmlHasStructuredInline)) {
    sanitized = inlineMd(plain).replace(/\r\n?/g, '\n').replace(/\n/g, '<br>');
  } else if (html.trim()) {
    sanitized = applyInlineMarkdown(sanitizeClipboardHtml(html));
  } else {
    sanitized = sanitizeClipboardText(plain);
  }

  if (!sanitized) return;
  sanitized = convertBraceVarsToPills(sanitized);

  // Separate BEFORE mutating the DOM so a sync `input` from execCommand
  // still lands as its own undo step (DocEditor owns Ctrl+Z).
  separateNextUndoStep();
  // Caret flush after a variable/mention pill + insertHTML can select or
  // unwrap the atom (pill looks whitened/broken). Park in a text node first.
  ensureEditableCaretForInsert();
  document.execCommand('insertHTML', false, sanitized);
  const el = e.currentTarget;
  if (el) {
    el.dispatchEvent(
      new InputEvent('input', { bubbles: true, inputType: 'insertFromPaste', data: null })
    );
  }
};
